import {ReactNode, StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import {App} from './App';
import {Authentication, AuthenticationProfile} from './services/authentication';
import {Communication} from './services/communication';
import {Datastores} from './services/datastores';
import {ApplicationJSON, ConfigJSON, Settings} from './services/settings';
import {SwwcInformationBar} from '@com.siemens.plm.horizon/swwc/bindings/react';
import './index.css';

class Main {
  static #initReact(isProduction: boolean): void {
    Datastores.init();
    this.#renderRoot(isProduction, <App />);
  }

  static #initLogin(login: unknown, _isProduction: boolean): void {
    if (typeof login !== 'string' || login.length === 0) throw new Error('Invalid login options');
    window.location.href = `${login}?mode=cookie`;
  }

  static #initFailure(isProduction: boolean, reason: string): void {
    this.#renderRoot(
      isProduction,
      <SwwcInformationBar
        closable={false}
        buttonText='Reload'
        fullWidthMode={true}
        informationText={reason}
        label='Failure'
        useIcon={true}
        onClickEvent={() => window.location.reload()}
        variant='error'
      />,
    );
  }

  static #renderRoot(isProduction: boolean, render: ReactNode): void {
    const rootElement = document.getElementById('root');
    createRoot(rootElement === null ? document.body : rootElement).render(
      isProduction ? render : <StrictMode>{render}</StrictMode>,
    );
  }

  static async #getConfig(): Promise<ConfigJSON | Error> {
    const request = await Communication.fetch<ConfigJSON, false>(`${window.location.origin}/config.json`, 'GET', 'json+object');
    if (request.status === 'error')
      return new Error('Missing "config.json" file, unable to determine authentication requirement(s)');
    const {api, auth} = request.data;
    if (
      typeof (api as unknown) !== 'string' ||
      api.length === 0 ||
      auth === null ||
      (auth as unknown) === true ||
      (auth !== false && typeof auth !== 'object')
    )
      return new Error('Invalid "config.json" payload');
    if (auth === false) return request.data;
    if (
      typeof (auth.login as unknown) !== 'string' ||
      auth.login.length === 0 ||
      typeof (auth.extend as unknown) !== 'string' ||
      auth.extend.length === 0 ||
      typeof (auth.logout as unknown) !== 'string' ||
      auth.logout.length === 0
    )
      return new Error('Invalid "config.json" authentication payload');
    return request.data;
  }

  static async #getApplication(): Promise<ApplicationJSON | Error> {
    const request = await Communication.fetch<ApplicationJSON, false>(
      `${window.location.origin}/application.json`,
      'GET',
      'json+object',
    );
    if (request.status === 'error')
      return new Error('Missing "application.json" file, unable to determine application information');
    return request.data;
  }

  static async init(): Promise<void> {
    const config = await this.#getConfig();
    if (config instanceof Error) {
      console.error(config);
      this.#initFailure(true, config.message);
      return;
    }
    Settings.configuration = config;
    const application = await this.#getApplication();
    if (application instanceof Error) {
      console.error(application);
      this.#initFailure(config.production, application.message);
      return;
    }
    Settings.application = application;
    console.info('Application configuration:', JSON.parse(JSON.stringify(application)), JSON.parse(JSON.stringify(config)));

    Communication.init(config.api);
    if (config.auth === false) {
      const me = await Communication.fetch<AuthenticationProfile, false>('me', 'GET', 'json+object');
      if (me.status === 'error') {
        console.error('Service query', me);
        this.#initFailure(config.production, 'Service query for profile failed');
        return;
      }
      Authentication.profile = me.data;
      this.#initReact(config.production);
      return;
    }
    const auth = await Communication.fetch<{access_token?: string}, false>(
      config.auth.extend,
      'GET',
      'json+object',
      undefined,
      {
        mode: 'all',
      },
    );
    if (auth.status === 'error' && auth.code === null) {
      console.error('Service query', auth);
      this.#initFailure(config.production, 'Service query for authentication failed');
      return;
    }
    if (auth.status === 'error' || typeof auth.data.access_token !== 'string' || auth.data.access_token.length === 0) {
      this.#initLogin(config.auth.login, config.production);
      return;
    }
    Authentication.init({extend: config.auth.extend, logout: config.auth.logout}, auth.data.access_token);
    this.#initReact(config.production);
  }
}

Main.init().catch((err) => console.error('Application error:', err));
